import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { ClipboardIcon, CloseIcon, XCircleIcon } from './Icons';
import '../styles/PromotionReview.css';

interface PromotionRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  image?: string;
  createdBy: string;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
  tags: string[];
}

const PromotionReview: React.FC = () => {
  const [requests, setRequests] = useState<PromotionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<PromotionRequest | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');

  const API_URL = BACKEND_URL;
  const availableTags = ['hackathon', 'dance', 'fest', 'games', 'workshop', 'exhibition', 'seminar'];

  useEffect(() => {
    fetchPromotionRequests();
  }, [filterStatus]);

  const fetchPromotionRequests = async () => {
    try {
      setLoading(true);
      const endpoint = filterStatus === 'all'
        ? `${API_URL}/api/promotions/requests`
        : `${API_URL}/api/promotions/requests?status=${filterStatus}`;
      
      const response = await axios.get(endpoint);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch promotion requests', error);
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (request: PromotionRequest) => {
    setSelectedRequest(request);
    setSelectedTags(request.tags || []);
    setCustomTag('');
    setReviewNotes('');
    setShowReviewModal(true);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleAddCustomTag = () => {
    if (customTag.trim() && !selectedTags.includes(customTag.trim().toLowerCase())) {
      setSelectedTags([...selectedTags, customTag.trim().toLowerCase()]);
      setCustomTag('');
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      await axios.put(`${API_URL}/api/promotions/${selectedRequest.id}/approve`, {
        tags: selectedTags,
        notes: reviewNotes
      });

      setShowReviewModal(false);
      fetchPromotionRequests();
      alert('Promotion approved and published!');
    } catch (error) {
      console.error('Failed to approve promotion', error);
      alert('Failed to approve promotion');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      await axios.put(`${API_URL}/api/promotions/${selectedRequest.id}/reject`, {
        notes: reviewNotes
      });

      setShowReviewModal(false);
      fetchPromotionRequests();
      alert('Promotion rejected.');
    } catch (error) {
      console.error('Failed to reject promotion', error);
      alert('Failed to reject promotion');
    }
  };

  return (
    <div className="promotion-review-page">
      <motion.div
        className="review-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1><ClipboardIcon size={24} /> Promotion Review Dashboard</h1>
        <p>Review and manage promotion requests from students and clubs</p>
      </motion.div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        {(['all', 'pending', 'approved', 'rejected'] as const).map(status => (
          <motion.button
            key={status}
            className={`filter-tab ${filterStatus === status ? 'active' : ''}`}
            onClick={() => setFilterStatus(status)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {filterStatus === status && (
              <motion.div
                className="tab-underline"
                layoutId="underline"
              />
            )}
          </motion.button>
        ))}
      </div>

      {/* Requests List */}
      <div className="requests-container">
        {loading ? (
          <div className="loading">Loading promotion requests...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <p>No {filterStatus === 'all' ? '' : filterStatus} promotion requests</p>
          </div>
        ) : (
          <motion.div className="requests-grid">
            {requests.map((request, index) => (
              <motion.div
                key={request.id}
                className={`request-card status-${request.status}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {request.image && (
                  <div className="request-image">
                    <img src={request.image} alt={request.title} />
                    <span className={`status-badge ${request.status}`}>
                      {request.status.toUpperCase()}
                    </span>
                  </div>
                )}

                <div className="request-content">
                  <h3>{request.title}</h3>
                  <p className="category">{request.category}</p>
                  <p className="description">{request.description}</p>

                  {request.tags.length > 0 && (
                    <div className="tags-display">
                      {request.tags.map(tag => (
                        <span key={tag} className="tag-badge">{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="request-meta">
                    <span className="creator">By: {request.createdBy}</span>
                    <span className="date">{new Date(request.createdAt).toLocaleDateString()}</span>
                  </div>

                  {request.status === 'pending' && (
                    <motion.button
                      className="review-btn"
                      onClick={() => openReviewModal(request)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Review & Tag →
                    </motion.button>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedRequest && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowReviewModal(false)}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Review Promotion Request</h2>
              <motion.button
                className="close-btn"
                onClick={() => setShowReviewModal(false)}
                whileHover={{ scale: 1.1, rotate: 90 }}
              >
                <CloseIcon size={14} />
              </motion.button>
            </div>

            <div className="modal-body">
              {selectedRequest.image && (
                <div className="preview-image">
                  <img src={selectedRequest.image} alt={selectedRequest.title} />
                </div>
              )}

              <div className="request-details">
                <h3>{selectedRequest.title}</h3>
                <p><strong>Category:</strong> {selectedRequest.category}</p>
                <p><strong>Description:</strong> {selectedRequest.description}</p>
                <p><strong>Submitted by:</strong> {selectedRequest.createdBy}</p>
                <p><strong>Date:</strong> {new Date(selectedRequest.createdAt).toLocaleDateString()}</p>
              </div>

              {/* Tag Selection */}
              <div className="tag-section">
                <h4>Select Tags for this Promotion</h4>
                <div className="tag-options">
                  {availableTags.map(tag => (
                    <motion.button
                      key={tag}
                      className={`tag-option ${selectedTags.includes(tag) ? 'selected' : ''}`}
                      onClick={() => handleTagToggle(tag)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {tag}
                      {selectedTags.includes(tag) && ' ✓'}
                    </motion.button>
                  ))}
                </div>

                {/* Custom Tag Input */}
                <div className="custom-tag-input">
                  <input
                    type="text"
                    value={customTag}
                    onChange={(e) => setCustomTag(e.target.value)}
                    placeholder="Add custom tag..."
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
                  />
                  <motion.button
                    onClick={handleAddCustomTag}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Add Tag
                  </motion.button>
                </div>

                {/* Selected Tags Display */}
                {selectedTags.length > 0 && (
                  <div className="selected-tags">
                    <p><strong>Selected Tags:</strong></p>
                    <div className="tags-list">
                      {selectedTags.map(tag => (
                        <span key={tag} className="selected-tag">
                          {tag}
                          <button
                            onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                            className="remove-tag"
                          >
                            <CloseIcon size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Review Notes */}
              <div className="notes-section">
                <label>Review Notes (Optional)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add any notes about this promotion..."
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                <motion.button
                  className="approve-btn"
                  onClick={handleApprove}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✓ Approve & Publish
                </motion.button>
                <motion.button
                  className="reject-btn"
                  onClick={handleReject}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <XCircleIcon size={14} /> Reject
                </motion.button>
                <motion.button
                  className="cancel-btn"
                  onClick={() => setShowReviewModal(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default PromotionReview;
